<template>
    <section
        class="information"
        :class="{
            'information--has-background': !content.style.background.turnOff.value,
            'information--full-width': !content.style.background.turnOff.value && content.style.background.fullWidth.value,
        }"
        :style="{...generateStylingVariables(content.style)}"
    >
        <div
            class="information__inner"
            :class="{
                'information__inner--background': !content.style.background.turnOff.value,
                'information__inner--reverse-mb': content.reverseContentMobile.value,
                'information__inner--reverse-dt': content.reverseContentDesktop.value,
                'information__inner--vertical': content.verticalAlignContentDesktop.value,
                'information__inner--padding-bottom': content.items.length > 1,
                'container container--only-desktop': !content.style.background.fullWidth.value,
            }"
        >
            <div
                v-if="showContent"
                class="information__content"
            >
                <zoe-atom-text
                    v-if="stringIsNotEmpty(content.text.value)"
                    class="information__text"
                    :class="{
                        'information__text--center-align-desktop': content.verticalAlignContentDesktop.value && content.showItems.value,
                    }"
                    :item="content.text.value"
                />

                <zoe-atom-text
                    v-if="stringIsNotEmpty(content.subtext.value)"
                    class="information__subtext"
                    :class="{
                        'information__subtext--center-align-desktop': content.verticalAlignContentDesktop.value && content.showItems.value
                    }"
                    :item="content.subtext.value"
                />

                <template v-if="stringIsNotEmpty(content.button.value)">
                    <button
                        v-if="!content.verticalAlignContentDesktop.value && deviceType === 'desktop'"
                        class="information__button button"
                        :class="{
                            'information__button--align-desktop': !content.showItems.value,
                        }"
                        @click="buttonClick"
                    >
                        <zoe-atom-text
                            class="label"
                            :item="content.button.value"
                        />
                    </button>

                    <button
                        v-if="!content.showButtonBelowMediaMobile.value && deviceType === 'mobile'"
                        class="information__button button"
                        @click="buttonClick"
                    >
                        <zoe-atom-text
                            class="label"
                            :item="content.button.value"
                        />
                    </button>
                </template>
            </div>

            <div
                v-if="content.showItems.value"
                class="information__container"
                :class="{
                    'information__container--single-media': content.items.length === 1,
                }"
            >
                <swiper
                    class="information__wrapper"
                    :speed="300"
                    :autoplay="{
                        enabled: true,
                        delay: 4000,
                        disableOnInteraction: true
                    }"
                    :loop="content.items.length > 1"
                    effect="fade"
                    :fade-effect="{
                        crossFade: true
                    }"
                    :navigation="{
                        prevEl: `.information__button--prev-${identifier}`,
                        nextEl: `.information__button--next-${identifier}`
                    }"
                >
                    <swiper-slide
                        v-for="(item, index) in content.items"
                        :key="index"
                        class="information__slide"
                    >
                        <div
                            class="information__media-wrapper"
                            :class="{
                                'information__media-wrapper--border': content.showBorder.value,
                                'information__media-wrapper--single': content.items.length === 1,
                            }"
                        >
                            <zoe-atom-media
                                class="information__media"
                                :item="media(item)"
                            />
                        </div>
                    </swiper-slide>
                </swiper>
                <div
                    v-if="content.items.length > 1"
                    class="information__navigation"
                >
                    <div
                        class="information__button information__button--prev swiper-button-prev swiper-navigation-button-prev"
                        :class="`information__button--prev-${identifier}`"
                    />

                    <div class="information__navigation-border" />

                    <div
                        class="information__button information__button--next swiper-button-next swiper-navigation-button-next"
                        :class="`information__button--next-${identifier}`"
                    />
                </div>
            </div>

            <template v-if="stringIsNotEmpty(content.button.value)">
                <button
                    v-if="content.verticalAlignContentDesktop.value && deviceType === 'desktop'"
                    class="information__button button"
                    :class="{
                        'information__button--align-desktop': !content.showItems.value,
                    }"
                    @click="buttonClick"
                >
                    <zoe-atom-text
                        class="label"
                        :item="content.button.value"
                    />
                </button>

                <button
                    v-if="content.showButtonBelowMediaMobile.value && deviceType === 'mobile'"
                    class="information__button button"
                    @click="buttonClick"
                >
                    <zoe-atom-text
                        class="label"
                        :item="content.button.value"
                    />
                </button>
            </template>
        </div>
    </section>
</template>

<script>
import SwiperCore, { Autoplay, Navigation, EffectFade } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';

SwiperCore.use([Autoplay, Navigation, EffectFade]);

import ZoeAtomMedia from '@atoms/media/v1.0.0/zoe-atom-media.vue';
import ZoeAtomText from '@atoms/text/v1.0.0/zoe-atom-text.vue';
import Component from '../../../../../../resources/mixins/component/component.vue';
import scrollToSelector from '../../../../../../resources/mixins/utils/scroll-to-selector';
import { generateStylingVariables } from '../../../../../../resources/mixins/styling/styling';

export default {
    components: {
        Swiper,
        SwiperSlide,
        ZoeAtomText,
        ZoeAtomMedia
    },

    mixins: [ Component ],

    props: {
        identifier: {
            type: String,
            required: true,
        },
    },

    computed: {
        deviceType() {
            return this.$store.state.page.deviceType;
        },

        showContent() {
            const showTitle = this.stringIsNotEmpty(this.content.text.value);
            const showText = this.stringIsNotEmpty(this.content.subtext.value);
            const showButtonVertical = this.stringIsNotEmpty(this.content.button.value) && !this.content.verticalAlignContentDesktop.value;
            const showButtonBelowMedia = this.stringIsNotEmpty(this.content.button.value) && !this.content.showButtonBelowMediaMobile.value;

            return showTitle || showText || showButtonVertical || showButtonBelowMedia;
        },
    },

    mounted() {
        const buttons = this.$el.querySelectorAll('.button');
        for(const button of buttons) {
            const text = button.querySelector('.label > *');
            const position = text.style.textAlign;

            if (position === 'center') {
                button.style.marginLeft = 'auto';
                button.style.marginRight = 'auto';
            } else if (position === 'right') {
                button.style.marginLeft = 'auto';
            }
        }
    },

    methods: {
        generateStylingVariables,
        buttonClick() {
            const scrollOffset = this.getValue('general.logobar.position') === 'fixed' ? -160 : -60;

            scrollToSelector('.step__flow', scrollOffset);
        },
        media(item) {
            if (this.$store.state.page.deviceType === 'mobile' || this.mediaIsEmpty(item.mediaDesktop.value)) {
                return item.mediaMobile.value;
            }

            return item.mediaDesktop.value;
        },
    },
};
</script>

<style lang="scss">
@use 'step-information';

@layer plugin {
    @import 'swiper/swiper-bundle';
}
</style>

<style lang="scss" scoped>
.information {
    --background: v-bind('content.style.background.color.value');
    --navigation-background: v-bind('content.style.navigation.background.value');
    --navigation-arrow-color: v-bind('content.style.navigation.arrowColor.value');
    --navigation-border-color: v-bind('content.style.navigation.borderColor.value');
}
</style>
